import { IMsalContext } from "@azure/msal-react";
import axios from "axios";
import { getAccessToken } from "config/token";
import { NEXUS_API_BASE_URL } from "const";
import { IUiRuntimeConfig } from "./runtimeConfig";

export interface IUiSecretsResponse extends IUiRuntimeConfig {
  secret_key_for_id: string;
}

interface IUiSecretsEncryptedResponse {
  wrapped_key: string;
  iv: string;
  ciphertext: string;
}

let secretKeyForId: string | undefined;
let uiSecretsPromise: Promise<IUiSecretsResponse | null> | null = null;

export const setSecretKeyForId = (key: string): void => {
  secretKeyForId = key;
};

export const getSecretKeyForId = (): string | undefined => secretKeyForId;

export const resetUiSecrets = (): void => {
  secretKeyForId = undefined;
  uiSecretsPromise = null;
};

const b64 = (buf: ArrayBuffer) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (s: string) => Uint8Array.from(atob(s), c => c.charCodeAt(0));

/** POST /v1/nexus/ui-secrets — unwrap encrypted UI secrets and configure the encrypt/decrypt key. */
export const fetchUiSecrets = async (
  msalInstance: IMsalContext,
): Promise<IUiSecretsResponse | null> => {
  if (uiSecretsPromise) {
    return uiSecretsPromise;
  }

  uiSecretsPromise = (async () => {
    try {
      const { publicKey, privateKey } = await crypto.subtle.generateKey(
        {
          name: "RSA-OAEP",
          modulusLength: 2048,
          publicExponent: new Uint8Array([1, 0, 1]),
          hash: "SHA-256",
        },
        false, // private key cannot be exported
        ["encrypt", "decrypt"],
      );
      const spki = await crypto.subtle.exportKey("spki", publicKey);

      const token = await getAccessToken(msalInstance);
      const bearerToken = "Bearer " + (token?.token ?? "");
      const requestURL = `${NEXUS_API_BASE_URL}/nexus/ui-secrets`;

      const response = await axios.post(
        requestURL,
        { public_key: b64(spki) },
        { headers: { Authorization: bearerToken, "Content-Type": "application/json" } },
      );

      const res = response.data as IUiSecretsEncryptedResponse;
      const rawAes = await crypto.subtle.decrypt(
        { name: "RSA-OAEP" },
        privateKey,
        unb64(res.wrapped_key),
      );
      const aesKey = await crypto.subtle.importKey(
        "raw",
        rawAes,
        "AES-GCM",
        false,
        ["decrypt"],
      );
      const plain = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: unb64(res.iv) },
        aesKey,
        unb64(res.ciphertext),
      );

      const data = JSON.parse(new TextDecoder().decode(plain)) as IUiSecretsResponse;

      if (data?.secret_key_for_id) {
        setSecretKeyForId(data.secret_key_for_id);
      }

      return data ?? null;
    } catch (error) {
      console.error("Error fetching UI secrets:", error);
      uiSecretsPromise = null; // allow retry on next call
      return null;
    }
  })();

  return uiSecretsPromise;
};
